import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImportDialog, type ImportType } from "./ImportDialog";
import { Button, LinkButton } from "../../core/basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import axios from "axios";
import { useAppSelector } from "../../../redux/hooks";
import FileSaver from "file-saver";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";

const API_URL = getURLPrefix();

interface Props {
  defaultType?: ImportType;
  importModalOpen?: boolean;
  setImportModalOpen?: (open: boolean) => void;
  title?: string;
  showDownloadTemplate?: boolean;
  style?: React.CSSProperties;
}

export function ImportButton(props: Props) {
  const { t } = useTranslation(["merchandising"]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const queryClient = useQueryClient();
  const orgId = useAppSelector((state) => state.user.organization_id);

  const effectiveImportType = props.defaultType ?? "channel";
  const isOpen = props.importModalOpen ?? isDialogOpen;

  const { setImportModalOpen } = props;
  const reset = useCallback(() => {
    setIsDialogOpen(false);
    setImportModalOpen?.(false);
  }, [setImportModalOpen]);

  const makeTemplate = useMutation({
    mutationFn: async () => {
      const {
        data: { export_id: exportId },
      } = await api.post<{ export_id: string }>(`/api/custom_reports/`, {
        mp: marketPlace.marketPlace,
        report_type: "product_category_report",
        is_task: false,
        params: {},
      });
      for (let i = 0; i < 100; i++) {
        const { data: exportObj } = await api.get<{
          id: string;
          status: number;
          name: string;
        }>(`/exports/${exportId}/`);
        if (exportObj.status === 2) {
          const url = `${API_URL}/api/exports/${exportObj.id}/download/`;
          const response = await axios({
            method: "GET",
            url: url,
            responseType: "blob",
          });

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
          });

          FileSaver.saveAs(blob, `${exportObj.name}.xlsx`);
          return;
        }
        if ([3, 4].includes(exportObj.status)) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["exports"],
      });
    },
  });

  return (
    <VStack alignItems={"stretch"} gap="xsmall">
      <Button
        onPress={() => {
          setIsDialogOpen(true);
          props.setImportModalOpen?.(true);
        }}
        style={props.style}
      >
        {props.title ?? t(($) => $.merchandising["Import Products"])}
      </Button>
      {props.showDownloadTemplate && (
        <LinkButton
          className={css({ fontSize: "medium" })}
          isPending={makeTemplate.isPending}
          onPress={() => {
            makeTemplate.mutate();
          }}
        >
          {t(($) => $.merchandising["Download Template"])}
        </LinkButton>
      )}

      {isOpen && <ImportDialog isOpen={isOpen} onClose={reset} importType={effectiveImportType} selectedOrg={orgId} />}
    </VStack>
  );
}
